/**
 * Jamak products.
 *
 * Structural, non-translated data only: slugs, live URLs, accent colors,
 * image paths, stack tags (proper nouns) and the Lucide icon names each
 * feature card renders. All prose lives in the `projects` i18n namespace
 * (src/i18n/locales/{en,es}/projects.json) keyed by the same slug.
 *
 * Colour roles (see DESIGN.md -> Ink / Brand): a single vivid hex cannot
 * be a decorative fill, body text and a button background at once and
 * still clear 4.5:1. So each project carries `accent` (decoration only),
 * `accentText` (a darkened version legal as text on #f5f7fb) and the
 * `accentFill` / `onAccent` pair for filled buttons.
 *
 * `status` is `live` unless the product is not fully out. The index and
 * detail pages only surface a badge when status is not `live`.
 *
 * Order here is the order they render on /projects. `featured: true`
 * promotes a project into the large editorial spread at the top.
 */

export const projects = [
  {
    slug: 'caudal',
    url: 'https://caudalapp.com/',
    /* Bare host, used as the visible anchor text on the outbound link */
    host: 'caudalapp.com',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-blue)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: 'var(--glass-cyan-from)',
    /* Filled-button pair. */
    accentFill: '#0066cc',
    onAccent: '#ffffff',
    accentSoft: 'rgba(0, 102, 204, 0.10)',
    accentGlow: 'rgba(0, 102, 204, 0.22)',
    featured: true,
    status: 'live',
    platform: 'web',
    stack: ['Next.js', 'React', 'TypeScript', 'Supabase'],
    integrations: [],
    featureIcons: ['FileSpreadsheet', 'CheckCircle2', 'Landmark', 'Receipt', 'Building2', 'FileCheck'],
    /* Which Jamak service pages this project demonstrates — rendered as
       internal links in the "What we did" band. */
    services: ['saas', 'cro'],
    images: {
      cover: '/images/projects/caudal-cover.png',
      hero: '/images/projects/caudal-hero.png',
      gallery: [
        '/images/projects/caudal-shot-1.png',
        '/images/projects/caudal-shot-2.png',
        '/images/projects/caudal-shot-3.png'
      ]
    }
  },
  {
    slug: 'ezeo',
    url: 'https://ezeo.ai/',
    host: 'ezeo.ai',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-purple)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: 'var(--glass-lilac-from)',
    /* Filled-button pair. */
    accentFill: '#7928ca',
    onAccent: '#ffffff',
    accentSoft: 'rgba(121, 40, 202, 0.10)',
    accentGlow: 'rgba(121, 40, 202, 0.22)',
    status: 'live',
    platform: 'web',
    stack: ['React', 'TypeScript', 'Vite', 'Supabase'],
    integrations: ['Google Search Console', 'Google Analytics 4', 'Microsoft Clarity'],
    featureIcons: ['Bot', 'Radar', 'BarChart3', 'TrendingUp', 'PenLine', 'Search'],
    services: ['geo', 'seo'],
    images: {
      cover: '/images/projects/ezeo-cover.png',
      hero: '/images/projects/ezeo-hero.png',
      gallery: [
        '/images/projects/ezeo-shot-1.png',
        '/images/projects/ezeo-shot-2.png',
        '/images/projects/ezeo-shot-3.png'
      ]
    }
  },
  {
    slug: 'lanzalo',
    url: 'https://lanzalo.lat/',
    host: 'lanzalo.lat',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-orange)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: '#bc4d00',
    /* Filled-button pair. */
    accentFill: '#ff6900',
    onAccent: 'var(--ink)',
    accentSoft: 'rgba(255, 105, 0, 0.10)',
    accentGlow: 'rgba(255, 105, 0, 0.22)',
    status: 'early-beta',
    platform: 'web',
    stack: ['Next.js', 'React', 'TypeScript', 'Supabase'],
    integrations: [],
    featureIcons: ['Compass', 'Trophy', 'MessageCircle', 'LayoutGrid', 'Globe2', 'Languages'],
    services: ['seo', 'saas'],
    images: {
      cover: '/images/projects/lanzalo-cover.png',
      hero: '/images/projects/lanzalo-hero.png',
      gallery: [
        '/images/projects/lanzalo-shot-1.png',
        '/images/projects/lanzalo-shot-2.png',
        '/images/projects/lanzalo-shot-3.png'
      ]
    }
  },
  {
    slug: 'quiz-path',
    url: 'https://quizpathapp.com/',
    host: 'quizpathapp.com',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-indigo)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: '#5f61e7',
    /* Filled-button pair. */
    accentFill: '#6163ec',
    onAccent: '#ffffff',
    accentSoft: 'rgba(99, 102, 241, 0.10)',
    accentGlow: 'rgba(99, 102, 241, 0.22)',
    status: 'marketing',
    platform: 'shopify',
    stack: ['Remix', 'TypeScript', 'Shopify'],
    integrations: ['Shopify', 'Klaviyo'],
    featureIcons: ['GitBranch', 'ShoppingCart', 'Mail', 'BarChart3', 'MousePointerClick', 'Languages'],
    services: ['shopify', 'cro'],
    images: {
      cover: '/images/projects/quiz-path-cover.png',
      hero: '/images/projects/quiz-path-hero.png',
      gallery: [
        '/images/projects/quiz-path-shot-1.png',
        '/images/projects/quiz-path-shot-2.png',
        '/images/projects/quiz-path-shot-3.png'
      ]
    }
  },
  {
    slug: 'regio-placas',
    url: 'https://regioplacas.app/',
    host: 'regioplacas.app',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-green)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: '#008238',
    /* Filled-button pair. */
    accentFill: '#00d25b',
    onAccent: 'var(--ink)',
    accentSoft: 'rgba(0, 210, 91, 0.12)',
    accentGlow: 'rgba(0, 210, 91, 0.22)',
    status: 'testing',
    platform: 'mobile',
    stack: ['Expo', 'React Native', 'TypeScript', 'Supabase'],
    integrations: ['ICVNL'],
    featureIcons: ['Receipt', 'BellRing', 'FileCheck', 'Clock', 'ClipboardCheck', 'Car'],
    services: ['saas'],
    images: {
      cover: '/images/projects/regio-placas-cover.png',
      hero: '/images/projects/regio-placas-hero.png',
      gallery: [
        '/images/projects/regio-placas-shot-1.png',
        '/images/projects/regio-placas-shot-2.png',
        '/images/projects/regio-placas-shot-3.png'
      ]
    }
  },
  {
    slug: 'access-age',
    url: 'https://apps.shopify.com/accessage-age-verification',
    host: 'apps.shopify.com',
    /* Decorative only — chip fills, borders, glows. */
    accent: 'var(--color-pink)',
    /* Legal as text on the page ground (>=4.5:1). */
    accentText: '#d12d4d',
    /* Filled-button pair. */
    accentFill: '#ff375f',
    onAccent: 'var(--ink)',
    accentSoft: 'rgba(255, 55, 95, 0.10)',
    accentGlow: 'rgba(255, 55, 95, 0.22)',
    status: 'live',
    platform: 'shopify',
    stack: ['Remix', 'TypeScript', 'Shopify'],
    integrations: ['Shopify Theme app embed'],
    featureIcons: ['Accessibility', 'Keyboard', 'Puzzle', 'Gauge', 'FileCheck', 'ShoppingBag'],
    services: ['shopify'],
    images: {
      cover: '/images/projects/access-age-cover.png',
      hero: '/images/projects/access-age-hero.png',
      gallery: [
        '/images/projects/access-age-shot-1.png',
        '/images/projects/access-age-shot-2.png',
        '/images/projects/access-age-shot-3.png'
      ]
    }
  }
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);

/** Previous / next in `projects` order, wrapping at both ends. */
export const getProjectNeighbors = (slug) => {
  const index = projects.findIndex((p) => p.slug === slug);
  if (index === -1) return { previous: null, next: null };
  return {
    previous: projects[(index - 1 + projects.length) % projects.length],
    next: projects[(index + 1) % projects.length]
  };
};

/** Route to the Jamak service page each project's `services` entry names. */
export const servicePaths = {
  seo: '/services/seo',
  geo: '/services/geo',
  cro: '/services/cro',
  shopify: '/services/shopify',
  saas: '/services/saas'
};

export default projects;
